import Api2Talk from './api2talk';

// Endpoints na API do 2Talk (Api2Talk já inclui /api). Contrato lowercase nos DOIS sentidos
// (LowercaseCaseNamingPolicy): normalizers no recebimento, denormalizers no envio.
const LIST = 'marketing-list';
const BC = 'marketing-broadcast';

// ===================== normalizers (lowercase API -> camelCase front) =====================

// Desembrulha arrays que podem vir como array puro, como { data: [...] } ou { $values: [...] }
// (ReferenceHandler.Preserve do System.Text.Json). Nunca lança.
const asArrayDeep = (x) => Array.isArray(x) ? x : (x && Array.isArray(x.$values) ? x.$values : (x && Array.isArray(x.data) ? x.data : []));

// Recurrence pode chegar como objeto, como string JSON, ou null. Sempre devolve objeto (ou null).
const parseRecurrence = (r) => {
  if (!r) return null;
  if (typeof r === 'string') { try { return JSON.parse(r); } catch { return null; } }
  return r;
};

export const normalizeBroadcastListItem = (b = {}) => ({
  id: b.id,
  name: b.name,
  status: b.status,
  deliveryMethod: b.deliverymethod ?? b.deliveryMethod ?? 'whatsapp',
  template: b.template,
  category: b.category,
  sender: b.sender,
  list: b.listname ?? b.list,
  creator: b.creator,
  createdAt: b.createdat ?? b.createdAt,
  scheduledAt: b.scheduledat ?? b.scheduledAt,        // COALESCE(next_run_at, scheduled_at) = próxima execução
  scheduleMode: b.schedulemode ?? b.scheduleMode ?? 'now',
  recurrence: parseRecurrence(b.recurrence),
  lastRunId: b.lastrunid ?? b.lastRunId ?? null,
  duplicatedFromId: b.duplicatedfromid ?? b.duplicatedFromId ?? null,
  duplicatedAt: b.duplicatedat ?? b.duplicatedAt ?? null,
  total: b.totalrecipients ?? b.total ?? 0,
  sent: b.sentcount ?? b.sent ?? 0,
});

// Opção de remetente/disparador SMS (transparência da escolha do provedor).
export const normalizeSmsSender = (s = {}) => ({
  configId: s.configid ?? s.configId ?? null,
  providerName: s.providername ?? s.providerName ?? '',
  providerCode: s.providercode ?? s.providerCode ?? '',
  pricePerSegment: s.pricepersegment ?? s.pricePerSegment ?? null,
});

// Opção de disparador de e-mail (transparência). senderId null = "Padrão Mexx" (infra compartilhada).
export const normalizeEmailSender = (s = {}) => ({
  senderId: s.senderid ?? s.senderId ?? null,
  name: s.name ?? '',
  fromMail: s.frommail ?? s.fromMail ?? '',
  isDefault: s.isdefault ?? s.isDefault ?? false,
  dailyLimit: s.dailylimit ?? s.dailyLimit ?? null,
  isMexxDefault: s.ismexxdefault ?? s.isMexxDefault ?? false,
});

// Disparador de e-mail no CRUD (modal "Configurar disparador"). Nunca traz a senha (só hasPassword).
export const normalizeEmailSenderRow = (s = {}) => ({
  id: s.id,
  name: s.name ?? '',
  fromMail: s.frommail ?? s.fromMail ?? '',
  hostMail: s.hostmail ?? s.hostMail ?? '',
  portMail: s.portmail ?? s.portMail ?? null,
  userMail: s.usermail ?? s.userMail ?? '',
  enableSsl: s.enablessl ?? s.enableSsl ?? true,
  dailyLimit: s.dailylimit ?? s.dailyLimit ?? null,
  isDefault: s.isdefault ?? s.isDefault ?? false,
  active: s.active ?? true,
  hasPassword: s.haspassword ?? s.hasPassword ?? false,
});

const denormalizeEmailSender = (m = {}) => ({
  name: m.name ?? '',
  frommail: m.fromMail ?? '',
  hostmail: m.hostMail ?? '',
  portmail: m.portMail != null && m.portMail !== '' ? Number(m.portMail) : null,
  usermail: m.userMail ?? '',
  password: m.password ?? '',          // vazio no update = mantém a atual (backend respeita)
  enablessl: m.enableSsl ?? true,
  dailylimit: m.dailyLimit != null && m.dailyLimit !== '' ? Number(m.dailyLimit) : null,
  isdefault: m.isDefault ?? false,
  active: m.active ?? true,
});

// Resumo de uma execução (run) — para o seletor no relatório.
export const normalizeRun = (r = {}) => ({
  id: r.id,
  runNumber: r.runnumber ?? r.runNumber ?? 0,
  status: r.status,
  at: r.at ?? null,
  total: r.totalrecipients ?? r.total ?? 0,
  sent: r.sentcount ?? r.sent ?? 0,
  failed: r.failedcount ?? r.failed ?? 0,
});

const normalizeVarMappingItem = (v = {}) => ({
  placeholder: v.placeholder,
  mode: v.mode ?? 'field',
  field: v.field ?? '',
  fixed: v.fixed ?? '',
  fallback: v.fallback ?? '',
});

const normalizeRecurrence = (raw) => {
  const r = parseRecurrence(raw) || {};
  return {
    freq: r.freq ?? 'weekly',
    time: r.time ?? '09:00',
    interval: r.interval ?? 1,
    weekdays: asArrayDeep(r.weekdays),
    monthlyMode: r.monthlymode ?? r.monthlyMode ?? 'dayOfMonth',
    dayOfMonth: r.dayofmonth ?? r.dayOfMonth ?? 1,
    nth: r.nth ?? 1,
    nthWeekday: r.nthweekday ?? r.nthWeekday ?? 0,
    endMode: r.endmode ?? r.endMode ?? 'never',
    endCount: r.endcount ?? r.endCount ?? 5,
    endDate: r.enddate ?? r.endDate ?? '',
  };
};

export const normalizeBroadcastDetail = (b = {}) => ({
  id: b.id,
  name: b.name,
  status: b.status,
  deliveryMethod: b.deliverymethod ?? b.deliveryMethod ?? 'whatsapp',
  messageBody: b.messagebody ?? b.messageBody ?? '',
  emailMode: b.emailmode ?? b.emailMode ?? 'custom',
  emailTemplateId: b.emailtemplateid ?? b.emailTemplateId ?? '',
  emailSubject: b.emailsubject ?? b.emailSubject ?? '',
  emailBodyHtml: b.emailbodyhtml ?? b.emailBodyHtml ?? '',
  smsConfigId: b.smsconfigid ?? b.smsConfigId ?? '',
  emailSenderId: b.emailsenderid ?? b.emailSenderId ?? '',
  attachments: asArrayDeep(b.attachments).map((a) => ({
    attachmentId: a.attachmentid ?? a.attachmentId ?? null,
    name: a.name,
    contentType: a.contenttype ?? a.contentType ?? '',
    size: a.size ?? null,
    path: a.path ?? null,
  })),
  channelId: b.channelid ?? b.channelId ?? '',
  senderId: b.registrationnumberrequestid ?? b.registrationNumberRequestId ?? '',
  templateId: b.templatemessageid ?? b.templateMessageId ?? '',
  language: b.templatelanguage ?? b.templateLanguage ?? '',
  category: b.templatecategory ?? b.templateCategory ?? '',
  optinTemplateId: b.optintemplateid ?? b.optInTemplateId ?? '',
  listId: b.marketinglistid ?? b.marketingListId ?? '',
  varMapping: asArrayDeep(b.variablemapping ?? b.variableMapping).map(normalizeVarMappingItem),
  schedule: {
    mode: b.schedulemode ?? b.scheduleMode ?? 'now',
    onceAt: b.scheduledat ?? b.scheduledAt ?? '',
    timezone: b.timezone ?? '',
    quietHours: b.quiethours ?? b.quietHours ?? false,
    slice: b.sliceenabled ?? b.sliceEnabled ?? false,
    sliceBatch: b.slicebatch ?? b.sliceBatch ?? 2000,
    sliceInterval: b.sliceintervalmin ?? b.sliceIntervalMin ?? 15,
    recurrence: normalizeRecurrence(b.recurrence),
  },
  duplicatedFromId: b.duplicatedfromid ?? b.duplicatedFromId ?? null,
  duplicatedAt: b.duplicatedat ?? b.duplicatedAt ?? null,
});

export const normalizeList = (l = {}) => ({
  id: l.id,
  name: l.name,
  type: l.type,
  total: l.totalcount ?? l.total ?? 0,
  valid: l.validcount ?? l.valid ?? 0,
  createdAt: l.createdat ?? l.createdAt,
});

export const normalizeListDetail = (l = {}) => ({
  id: l.id,
  name: l.name,
  type: l.type,
  criteria: {
    tags: l.criteria?.tags ?? [],
    organizationIds: l.criteria?.organizationids ?? l.criteria?.organizationIds ?? [],
    addressIds: l.criteria?.addressids ?? l.criteria?.addressIds ?? [],
  },
  total: l.totalcount ?? 0,
  valid: l.validcount ?? 0,
});

// Resultado da busca da base elegível (seleção manual / preview de segmentação)
export const normalizeEligibleContact = (c = {}) => {
  const sourceType = c.sourcetype ?? c.sourceType;
  const sourceId = c.sourceid ?? c.sourceId;
  return {
    id: `${sourceType}_${sourceId}`,
    sourceType,
    sourceId,
    name: c.name,
    phone: c.phone,
    email: c.email,
    document: c.document,
    valid: c.valid ?? false,
  };
};

// Contato já materializado de uma lista (preview da lista salva)
export const normalizeContactRow = (c = {}) => ({
  id: c.id,
  name: c.name,
  phone: c.phone,
  valid: c.valid ?? false,
});

export const normalizeLocation = (a = {}) => ({ id: a.id, name: a.name });

export const normalizeReport = (r = {}) => ({
  name: r.name,
  status: r.status,
  runId: r.runid ?? r.runId ?? null,
  category: r.category,
  duplicatedFromId: r.duplicatedfromid ?? r.duplicatedFromId ?? null,
  duplicatedAt: r.duplicatedat ?? r.duplicatedAt ?? null,
  costUnit: r.costunit ?? r.costUnit ?? 0,
  totals: {
    valid: r.totals?.valid ?? 0,
    invalid: r.totals?.invalid ?? 0,
    sent: r.totals?.sent ?? 0,
    delivered: r.totals?.delivered ?? 0,
    read: r.totals?.read ?? 0,
    replied: r.totals?.replied ?? 0,
    failed: r.totals?.failed ?? 0,
  },
  timeline: r.timeline ?? [],
  totalContacts: r.totalcontacts ?? r.totalContacts ?? 0,
  deliveryMethod: r.deliverymethod ?? r.deliveryMethod ?? 'whatsapp',
  emailDailyLimitPaused: r.emaildailylimitpaused ?? r.emailDailyLimitPaused ?? false,
  emailDailyQuota: r.emaildailyquota ?? r.emailDailyQuota ?? null,
  emailSentToday: r.emailsenttoday ?? r.emailSentToday ?? null,
  contacts: (r.contacts ?? []).map((c) => ({
    id: c.id, phone: c.phone, email: c.email ?? '', name: c.name, status: c.status, at: c.at, error: c.error,
    hasMessage: c.hasmessage ?? c.hasMessage ?? false,
  })),
  audit: (r.audit ?? []).map((a) => ({
    icon: a.action, who: a.who, at: a.at, actKey: `marketing.audit.${a.action}`,
  })),
});

// Mensagem efetivamente disparada para um contato (lowercase -> camelCase).
export const normalizeSentMessage = (m = {}) => ({
  name: m.name,
  phone: m.phone,
  email: m.email ?? '',
  deliveryMethod: m.deliverymethod ?? m.deliveryMethod ?? 'whatsapp',
  subject: m.subject ?? '',
  status: m.status,
  body: m.body ?? '',
  providerResponse: m.providerresponse ?? m.providerResponse ?? '',
  sentAt: m.sentat ?? m.sentAt ?? null,
});

// ===================== denormalizers (camelCase front -> lowercase API) =====================

const denormalizeBroadcast = (m = {}, action) => {
  const s = m.schedule ?? {};
  const rec = s.recurrence ?? {};
  return {
    name: m.name,
    deliverymethod: m.deliveryMethod || 'whatsapp',
    messagebody: m.messageBody || null,
    emailmode: m.emailMode || null,
    emailtemplateid: m.emailTemplateId || null,
    emailsubject: m.emailSubject || null,
    emailbodyhtml: m.emailBodyHtml || null,
    smsconfigid: m.smsConfigId || null,
    emailsenderid: m.emailSenderId || null,
    attachments: (m.attachments ?? []).map((a) => ({
      attachmentid: a.attachmentId ?? a.attachmentid ?? null,
      name: a.name,
      contenttype: a.contentType ?? a.contenttype ?? null,
      size: a.size ?? null,
      path: a.path ?? null,
    })),
    channelid: m.channelId || null,
    registrationnumberrequestid: m.senderId || null,
    templatemessageid: m.templateId || null,
    templatelanguage: m.language || null,
    optintemplateid: m.optinTemplateId || null,
    marketinglistid: m.listId || null,
    variablemapping: (m.varMapping ?? []).map((v) => ({
      placeholder: v.placeholder,
      mode: v.mode,
      field: v.field || null,
      fixed: v.fixed || null,
      fallback: v.fallback || null,
    })),
    schedulemode: s.mode || 'now',
    scheduledat: s.mode === 'once' ? (s.onceAt || null) : null,
    recurrence: s.mode === 'recurring' ? {
      freq: rec.freq,
      time: rec.time,
      interval: rec.interval,
      weekdays: rec.weekdays,
      monthlymode: rec.monthlyMode,
      dayofmonth: rec.dayOfMonth,
      nth: rec.nth,
      nthweekday: rec.nthWeekday,
      endmode: rec.endMode || 'never',
      endcount: rec.endMode === 'count' ? (rec.endCount || 1) : null,
      enddate: rec.endMode === 'date' ? (rec.endDate || null) : null,
    } : null,
    timezone: s.timezone || null,
    quiethours: !!s.quietHours,
    sliceenabled: !!s.slice,
    slicebatch: s.sliceBatch ?? 2000,
    sliceintervalmin: s.sliceInterval ?? 15,
    action: action || 'draft',
  };
};

const denormalizeList = (m = {}) => {
  const isSegment = m.type === 'segment';
  return {
    name: m.name,
    type: m.type,
    criteria: isSegment ? {
      tags: m.criteria?.tags ?? [],
      organizationids: m.criteria?.organizationIds ?? [],
      addressids: m.criteria?.addressIds ?? [],
    } : null,
    contacts: !isSegment ? (m.contacts ?? []).map((c) => ({
      sourcetype: c.sourceType ?? 'manual',
      sourceid: c.sourceId ?? null,
      name: c.name ?? null,
      phone: c.phone ?? null,
      email: c.email ?? null,
      document: c.document ?? null,
      extrafields: c.extraFields ?? null,
    })) : null,
  };
};

const buildSearchRequest = (criteria, search, page, pageSize) => ({
  criteria: criteria ? {
    tags: criteria.tags ?? [],
    organizationids: criteria.organizationIds ?? [],
    addressids: criteria.addressIds ?? [],
  } : null,
  search: search || null,
  page: page || 1,
  pagesize: pageSize || 20,
});

// ===================== service =====================

const MarketingService = {
  // ---- transmissões ----
  listBroadcasts: (status, page = 1, pageSize = 10, deliveryMethod) =>
    Api2Talk.get(`${BC}?page=${page}&pageSize=${pageSize}${status ? `&status=${status}` : ''}${deliveryMethod ? `&deliveryMethod=${deliveryMethod}` : ''}`),
  getBroadcastCounts: () => Api2Talk.get(`${BC}/counts`),
  getSmsCost: () => Api2Talk.get(`${BC}/sms-cost`),
  getSmsSenders: () => Api2Talk.get(`${BC}/sms-senders`),
  // Disparadores de e-mail: opções pro seletor (Padrão Mexx + próprios) e CRUD leve do modal.
  getEmailSenders: () => Api2Talk.get(`${BC}/email-senders`),
  listEmailSenders: () => Api2Talk.get(`${BC}/email-senders/manage`),
  createEmailSender: (model) => Api2Talk.post(`${BC}/email-senders`, denormalizeEmailSender(model)),
  updateEmailSender: (id, model) => Api2Talk.put(`${BC}/email-senders/${id}`, denormalizeEmailSender(model)),
  deleteEmailSender: (id) => Api2Talk.delete(`${BC}/email-senders/${id}`),
  testEmailSender: (id, model, destination) => Api2Talk.post(`${BC}/email-senders/test`, { id: id || null, sender: denormalizeEmailSender(model), destination }),
  getBroadcast: (id) => Api2Talk.get(`${BC}/${id}`),
  createBroadcast: (model, action) => Api2Talk.post(`${BC}`, denormalizeBroadcast(model, action)),
  updateBroadcast: (id, model, action) => Api2Talk.put(`${BC}/${id}`, denormalizeBroadcast(model, action)),
  deleteBroadcast: (id) => Api2Talk.delete(`${BC}/${id}`),
  bulkDeleteBroadcasts: (ids) => Api2Talk.post(`${BC}/bulk-delete`, ids),
  activateBroadcast: (id) => Api2Talk.post(`${BC}/${id}/activate`),
  scheduleBroadcast: (id) => Api2Talk.post(`${BC}/${id}/schedule`),
  pauseBroadcast: (id) => Api2Talk.post(`${BC}/${id}/pause`),
  resumeBroadcast: (id) => Api2Talk.post(`${BC}/${id}/resume`),
  finishBroadcast: (id) => Api2Talk.post(`${BC}/${id}/finish`),
  revertToDraft: (id) => Api2Talk.post(`${BC}/${id}/to-draft`),
  duplicateBroadcast: (id) => Api2Talk.post(`${BC}/${id}/duplicate`),
  testBroadcast: (id, destination) => Api2Talk.post(`${BC}/${id}/test`, { destination }),
  getReport: (id, page = 1, pageSize = 50, status, runId) =>
    Api2Talk.get(`${BC}/${id}/report?page=${page}&pageSize=${pageSize}${status ? `&status=${status}` : ''}${runId ? `&runId=${runId}` : ''}`),
  getBroadcastRuns: (id) => Api2Talk.get(`${BC}/${id}/runs`),
  exportReport: (id, runId) => Api2Talk.get(`${BC}/${id}/export${runId ? `?runId=${runId}` : ''}`, { responseType: 'blob' }),
  exportBroadcastContacts: (id) => Api2Talk.get(`${BC}/${id}/contacts-csv`, { responseType: 'blob' }),
  getSentMessage: (id, recipientId) => Api2Talk.get(`${BC}/${id}/recipient/${recipientId}/message`),

  // ---- listas ----
  listLists: () => Api2Talk.get(`${LIST}`),
  getList: (id) => Api2Talk.get(`${LIST}/${id}`),
  createList: (model) => Api2Talk.post(`${LIST}`, denormalizeList(model)),
  updateList: (id, model) => Api2Talk.put(`${LIST}/${id}`, denormalizeList(model)),
  deleteList: (id) => Api2Talk.delete(`${LIST}/${id}`),
  duplicateList: (id) => Api2Talk.post(`${LIST}/${id}/duplicate`),
  getListContacts: (id, page = 1, pageSize = 20) => Api2Talk.get(`${LIST}/${id}/contacts?page=${page}&pageSize=${pageSize}`),
  getNoOptInCount: (listId, registrationNumberRequestId) => Api2Talk.get(`${LIST}/${listId}/no-optin-count?registrationNumberRequestId=${registrationNumberRequestId}`),
  getListContactsForEdit: (id) => Api2Talk.get(`${LIST}/${id}/contacts-edit`),
  exportListCsv: (id) => Api2Talk.get(`${LIST}/${id}/export-csv`, { responseType: 'blob' }),
  searchContacts: (criteria, search, page = 1, pageSize = 20) =>
    Api2Talk.post(`${LIST}/search-contacts`, buildSearchRequest(criteria, search, page, pageSize)),
  parseCsv: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return Api2Talk.post(`${LIST}/parse-csv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getLocations: (orgIds) =>
    Api2Talk.get(`${LIST}/locations?${(orgIds ?? []).map((id) => `organizationIds=${id}`).join('&')}`),
  getTags: () => Api2Talk.get(`${LIST}/tags`),
  downloadSegmentCsv: (criteria) => Api2Talk.post(`${LIST}/segment-csv`, {
    tags: criteria?.tags ?? [],
    organizationids: criteria?.organizationIds ?? [],
    addressids: criteria?.addressIds ?? [],
  }, { responseType: 'blob' }),
};

export default MarketingService;
